import { useState } from "react";
import * as d3 from "d3";
import { useChartDimensions } from "../../../components/graphs/useChartDimensions";
import { NEUTRAL, OKABE_ITO } from "../../../config/vizPalette";
import { Card, InsightSection, QueryState } from "./InsightSection";
import { useCommunity } from "./useSteamInsights";

// "Commonly earned": the median achievement in the game is held by at least half of players
const COMMON_THRESHOLD = 50;

const isOpportunity = (game) => game.median_global_pct >= COMMON_THRESHOLD && (game.completion_pct ?? 0) < 100;

const pct = (value) => (value == null ? "–" : `${value.toFixed(0)}%`);

const Scatter = ({ games }) => {
    const [ref, dimensions] = useChartDimensions({
        height: 360,
        marginTop: 16,
        marginRight: 16,
        marginBottom: 44,
        marginLeft: 56,
    });
    const [hovered, setHovered] = useState(null);

    const x = d3.scaleLinear().domain([0, 100]).range([0, dimensions.boundedWidth]);
    const y = d3.scaleLinear().domain([0, 100]).range([dimensions.boundedHeight, 0]);
    const r = d3
        .scaleSqrt()
        .domain([0, d3.max(games, (g) => g.playtime_minutes) || 1])
        .range([2.5, dimensions.boundedWidth < 480 ? 9 : 14]);
    const ticks = [0, 25, 50, 75, 100];

    // Draw big circles first so small ones stay clickable
    const ordered = [...games].sort((a, b) => b.playtime_minutes - a.playtime_minutes);

    return (
        <div ref={ref} className="relative w-full" style={{ height: 360 }}>
            {dimensions.boundedWidth > 0 && (
                <svg
                    width={dimensions.width}
                    height={dimensions.height}
                    role="img"
                    aria-label="Each game's median community unlock rate against your completion"
                >
                    <g transform={`translate(${dimensions.marginLeft}, ${dimensions.marginTop})`}>
                        {/* Opportunity zone: commonly earned, not finished */}
                        <rect
                            x={x(COMMON_THRESHOLD)}
                            y={y(100)}
                            width={x(100) - x(COMMON_THRESHOLD)}
                            height={y(0) - y(100)}
                            fill={OKABE_ITO.yellow}
                            opacity={0.18}
                        />
                        {ticks.map((tick) => (
                            <g key={tick}>
                                <line y1={0} y2={dimensions.boundedHeight} x1={x(tick)} x2={x(tick)} stroke="#e5e7eb" />
                                <line x1={0} x2={dimensions.boundedWidth} y1={y(tick)} y2={y(tick)} stroke="#e5e7eb" />
                                <text x={x(tick)} y={dimensions.boundedHeight + 18} textAnchor="middle" fontSize={12} fill="#6b7280">
                                    {tick}%
                                </text>
                                <text x={-8} y={y(tick)} dy="0.32em" textAnchor="end" fontSize={12} fill="#6b7280">
                                    {tick}%
                                </text>
                            </g>
                        ))}
                        <text
                            x={dimensions.boundedWidth / 2}
                            y={dimensions.boundedHeight + 38}
                            textAnchor="middle"
                            fontSize={12}
                            fill="#6b7280"
                        >
                            Median share of players with each achievement
                        </text>
                        <text
                            transform={`translate(${-dimensions.marginLeft + 14}, ${dimensions.boundedHeight / 2}) rotate(-90)`}
                            textAnchor="middle"
                            fontSize={12}
                            fill="#6b7280"
                        >
                            Your completion
                        </text>

                        {ordered.map((game) => (
                            <circle
                                key={game.appid}
                                cx={x(game.median_global_pct)}
                                cy={y(game.completion_pct ?? 0)}
                                r={r(game.playtime_minutes)}
                                fill={isOpportunity(game) ? OKABE_ITO.vermillion : OKABE_ITO.blue}
                                fillOpacity={hovered?.appid === game.appid ? 0.95 : 0.55}
                                stroke={hovered?.appid === game.appid ? "#111827" : "white"}
                                strokeWidth={1}
                                onPointerEnter={() => setHovered(game)}
                                onPointerDown={() => setHovered(game)}
                                onPointerLeave={() => setHovered(null)}
                            />
                        ))}
                    </g>
                </svg>
            )}
            {hovered && (
                <div
                    className="pointer-events-none absolute z-10 min-w-[11rem] rounded-md border border-gray-200 bg-white px-3 py-2 text-sm shadow"
                    style={{
                        top: 8,
                        ...(x(hovered.median_global_pct) > dimensions.boundedWidth / 2
                            ? { left: dimensions.marginLeft + 8 }
                            : { right: dimensions.marginRight + 8 }),
                    }}
                >
                    <div className="font-semibold text-gray-800">{hovered.name || `App ${hovered.appid}`}</div>
                    <div className="text-gray-700">
                        Completion {pct(hovered.completion_pct)} ({hovered.n_earned} of {hovered.n_achievements})
                    </div>
                    <div className="text-gray-700">Median community rate {pct(hovered.median_global_pct)}</div>
                    <div className="text-gray-700">{Math.round(hovered.playtime_minutes / 60)} hours played</div>
                </div>
            )}
        </div>
    );
};

export const CommunityScatter = () => {
    const query = useCommunity();
    const games = query.data || [];
    const opportunities = games
        .filter((game) => isOpportunity(game) && game.n_easy_remaining > 0)
        .sort((a, b) => b.n_easy_remaining - a.n_easy_remaining)
        .slice(0, 5);

    return (
        <InsightSection
            id="community"
            title="What the community earns"
            intro="Each circle is a game with achievements, sized by your playtime. Games to the right have achievements most players pick up; the shaded area holds those you haven't finished."
        >
            <Card>
                <QueryState query={query} isEmpty={!games.length} emptyText="No community unlock rates yet.">
                    <Scatter games={games} />
                    <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-sm text-gray-500">
                        <span className="inline-flex items-center gap-2">
                            <span aria-hidden="true" className="inline-block h-3 w-3 rounded-full" style={{ backgroundColor: OKABE_ITO.vermillion }} />
                            Commonly earned, not finished
                        </span>
                        <span className="inline-flex items-center gap-2">
                            <span aria-hidden="true" className="inline-block h-3 w-3 rounded-full" style={{ backgroundColor: OKABE_ITO.blue }} />
                            Other games
                        </span>
                        <span className="inline-flex items-center gap-2">
                            <span aria-hidden="true" className="inline-block h-3 w-3 rounded-full border" style={{ borderColor: NEUTRAL }} />
                            Size: hours played
                        </span>
                    </div>
                    {opportunities.length > 0 && (
                        <div className="mt-4">
                            <p className="text-sm font-semibold text-gray-700">Most commonly earned achievements still to get</p>
                            <ul className="mt-1 space-y-1 text-sm text-gray-700">
                                {opportunities.map((game) => (
                                    <li key={game.appid}>
                                        {game.name || `App ${game.appid}`}: {game.n_easy_remaining} achievement
                                        {game.n_easy_remaining === 1 ? "" : "s"} held by over half of players
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </QueryState>
            </Card>
        </InsightSection>
    );
};
